/**
 * @fileoverview Winbit32 Wallet Adapter
 *
 * Wraps the shared Zcash client package for NFPT:
 * - mnemonic-based wallet connection
 * - Orchard scan + balance retrieval
 * - shielded send flow
 */

import bs58check from 'bs58check';
import { Buffer } from 'buffer';
import { crypto as bitcoinCrypto } from 'bitcoinjs-lib';
import { HDKey } from '@scure/bip32';
import { mnemonicToSeedSync, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english.js';
import { secp256k1 } from '@noble/curves/secp256k1.js';
import {
	DEFAULT_NETWORK,
	ZATOSHIS_PER_ZEC,
	ZCASH_DERIVATION_PATH,
	configureWasm,
	generateWebZjsSeedPhrase,
	getShieldedTransactionClient,
	getWebZjsClient,
	type ShieldedBalance,
	type ShieldedTransactionClient,
	uninstallFetchInterceptor,
} from '@shared/zcash/index.ts';
import * as wasmExports from '@shared/zcash/wasm/webzjs_keys_and_send_fixed.js';
import type { SignedTransaction, ZcashAddress, ZcashBalance } from './zcashWallet';

export interface ScanProgress {
	isScanning: boolean;
	percent: number;
	status: string;
	scannedHeight?: number;
	latestHeight?: number;
	error?: string;
}

type ScanProgressListener = (progress: ScanProgress) => void;

const ZCASH_P2PKH_PREFIX = Buffer.from([0x1c, 0xb8]); // mainnet t1

const normaliseMnemonic = (value: string): string =>
	(value || '').trim().replace(/\s+/g, ' ');

const toZec = (zatoshis: bigint): number => Number(zatoshis) / ZATOSHIS_PER_ZEC;

/**
 * Pull a percentage out of a free-text status line such as
 * "Scanning notes... 45.0%" so the status bar can show a real progress
 * value during a send — the underlying client only emits strings.
 */
const parseStatusPercent = (status: string): number | null => {
	const match = /(\d+(?:\.\d+)?)\s*%/.exec(status || '');
	if (!match) return null;
	const pct = Number(match[1]);
	return Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : null;
};

const resolveZcashApiKey = (): string | undefined => {
	const globalKey = (globalThis as { __WB32_ZCASH_API_KEY__?: string }).__WB32_ZCASH_API_KEY__;
	if (typeof globalKey === 'string' && globalKey.trim().length > 0) {
		return globalKey.trim();
	}
	const viteKey = (import.meta as { env?: Record<string, unknown> }).env?.VITE_ZCASH_API_KEY;
	if (typeof viteKey === 'string' && viteKey.trim().length > 0) {
		return viteKey.trim();
	}
	return undefined;
};

class Winbit32WalletService {
	private client: ShieldedTransactionClient | null = null;
	private mnemonic: string | null = null;
	private addresses: ZcashAddress | null = null;
	private lastBalance: ShieldedBalance | null = null;
	private connected = false;
	private wasmConfigured = false;

	private scanProgress: ScanProgress = {
		isScanning: false,
		percent: 0,
		status: 'idle',
	};
	private readonly scanListeners = new Set<ScanProgressListener>();

	private async ensureWasmInitialized(): Promise<void> {
		if (this.wasmConfigured) return;
		configureWasm(wasmExports as any);
		await getWebZjsClient(DEFAULT_NETWORK).initialize();
		this.wasmConfigured = true;
	}

	private ensureWasmConfigured(): void {
		if (this.wasmConfigured) return;
		configureWasm(wasmExports as any);
		this.wasmConfigured = true;
	}

	private deriveTransparentAddress(mnemonic: string): string {
		const seed = mnemonicToSeedSync(mnemonic);
		const hdkey = HDKey.fromMasterSeed(seed);
		const childKey = hdkey.derive(ZCASH_DERIVATION_PATH);
		if (!childKey.privateKey) {
			throw new Error('Failed to derive transparent private key');
		}
		const publicKey = secp256k1.getPublicKey(childKey.privateKey, true);
		const pubKeyHash = bitcoinCrypto.hash160(Buffer.from(publicKey));
		return bs58check.encode(Buffer.concat([ZCASH_P2PKH_PREFIX, pubKeyHash]));
	}

	async connect(mnemonic: string): Promise<ZcashAddress> {
		const normalised = normaliseMnemonic(mnemonic);
		if (!validateMnemonic(normalised, wordlist)) {
			throw new Error('Invalid mnemonic phrase');
		}

		this.ensureWasmConfigured();
		this.client = getShieldedTransactionClient(DEFAULT_NETWORK);
		this.client.setApiKey(resolveZcashApiKey());
		await this.client.initialize();

		const webzjsClient = getWebZjsClient(DEFAULT_NETWORK);
		const { address } = await webzjsClient.generateUnifiedAddress(normalised, 0);

		let transparentAddress = '';
		try {
			transparentAddress = this.deriveTransparentAddress(normalised);
		} catch (err) {
			console.warn('Failed to derive transparent address, continuing with unified-only wallet:', err);
		}

		this.mnemonic = normalised;
		this.addresses = {
			shielded: address,
			transparent: transparentAddress,
		};
		this.connected = true;
		this.setScanProgress({
			isScanning: true,
			percent: 0,
			status: 'Scanning Orchard notes...',
			error: undefined,
		});

		// Start scan in the background so UI can connect immediately.
		void this.refreshBalance().catch((err: unknown) => {
			const message = err instanceof Error ? err.message : 'Unknown scan error';
			this.setScanProgress({
				isScanning: false,
				percent: 0,
				status: 'Scan failed',
				error: message,
			});
		});

		return this.addresses;
	}

	disconnect(): void {
		this.mnemonic = null;
		this.addresses = null;
		this.lastBalance = null;
		this.connected = false;
		this.client = null;
		uninstallFetchInterceptor();
		this.setScanProgress({
			isScanning: false,
			percent: 0,
			status: 'idle',
			scannedHeight: undefined,
			latestHeight: undefined,
			error: undefined,
		});
	}

	isConnected(): boolean {
		return this.connected;
	}

	getAddresses(): ZcashAddress | null {
		return this.addresses;
	}

	getBalance(): ZcashBalance {
		if (!this.lastBalance) {
			return { shielded: 0, transparent: 0, total: 0 };
		}
		const shieldedZat = this.lastBalance.orchard + this.lastBalance.sapling;
		const transparentZat = this.lastBalance.transparent;
		const shielded = toZec(shieldedZat);
		const transparent = toZec(transparentZat);
		return {
			shielded,
			transparent,
			total: shielded + transparent,
		};
	}

	async refreshBalance(): Promise<ZcashBalance> {
		if (!this.client || !this.mnemonic || !this.addresses) {
			throw new Error('Wallet not connected');
		}
		this.setScanProgress({
			isScanning: true,
			percent: 0,
			status: 'Scanning Orchard notes...',
			error: undefined,
		});

		this.lastBalance = await this.client.getShieldedBalance(
			this.mnemonic,
			this.addresses.shielded,
			this.addresses.transparent || undefined,
			0,
			undefined,
			(balance) => {
				const progress = balance.scanProgress;
				const percent = Number.isFinite(Number(progress.percentComplete))
					? Math.max(0, Math.min(100, Number(progress.percentComplete)))
					: 0;
				const current = Number(progress.currentBlock || 0);
				const latest = Number(progress.latestBlock || 0);
				const status = latest > 0
					? `Scanning blocks ${current}/${latest}...`
					: 'Scanning Orchard notes...';

				this.setScanProgress({
					isScanning: progress.isScanning !== false,
					percent,
					status,
					scannedHeight: current || undefined,
					latestHeight: latest || undefined,
				});
			},
		);

		this.setScanProgress({
			isScanning: false,
			percent: 100,
			status: 'Scan complete',
			error: undefined,
		});
		return this.getBalance();
	}

	async send(
		recipient: string,
		amountZec: number,
		memo?: string,
		options?: {
			allowTransparentInputs?: boolean;
			skipShieldedScan?: boolean;
			abortSignal?: AbortSignal;
		},
	): Promise<SignedTransaction> {
		if (!this.client || !this.mnemonic || !this.addresses) {
			throw new Error('Wallet not connected');
		}

		const skipShieldedScan = options?.skipShieldedScan === true;
		// Skipping the scan only makes sense if we're allowed to spend
		// transparent funds, so it implies allowTransparentInputs.
		const allowTransparentInputs =
			options?.allowTransparentInputs === true || skipShieldedScan;
		const abortSignal = options?.abortSignal;

		// Surface the send as ongoing activity so the global status bar lights
		// up instead of the caller showing a frozen "Picking…": the in-browser
		// note search + selection can take a while and must be visible.
		this.setScanProgress({
			isScanning: true,
			percent: 0,
			status: skipShieldedScan
				? 'Shielding transparent funds…'
				: allowTransparentInputs
					? 'Preparing transaction (auto-shielding if needed)…'
					: 'Preparing transaction…',
			error: undefined,
		});

		let txid: string;
		try {
			txid = await this.client.sendShieldedTransaction({
				mnemonic: this.mnemonic,
				fromAddress: this.addresses.shielded,
				transparentAddress: this.addresses.transparent || undefined,
				toAddress: recipient,
				amountZec,
				memo,
				allowTransparentInputs,
				skipShieldedScan,
				abortSignal,
				onStatus: (status) => {
					const pct = parseStatusPercent(status);
					this.setScanProgress({
						status,
						isScanning: true,
						percent: pct ?? this.scanProgress.percent,
					});
				},
				// Structured progress: feed the determinate bar + block height in
				// the global status bar during the mint note search (the status
				// text alone often only carries an elapsed counter on a cold cache).
				onProgress: (p) => {
					const percent = Number.isFinite(Number(p.percent))
						? Math.max(0, Math.min(100, Number(p.percent)))
						: this.scanProgress.percent;
					this.setScanProgress({
						isScanning: true,
						percent,
						scannedHeight: p.currentBlock ?? this.scanProgress.scannedHeight,
						latestHeight: p.latestBlock ?? this.scanProgress.latestHeight,
					});
				},
			});
		} catch (error) {
			const message = error instanceof Error ? error.message : 'Transaction failed';
			// An abort (user chose to shield from transparent instead of waiting
			// for the scan) isn't a failure — keep the bar calm; the caller will
			// immediately start the replacement send.
			if (abortSignal?.aborted) {
				this.setScanProgress({
					isScanning: true,
					percent: 0,
					status: 'Switching to transparent funds…',
					error: undefined,
				});
			} else {
				this.setScanProgress({
					isScanning: false,
					percent: 0,
					status: 'Transaction failed',
					error: message,
				});
			}
			throw error;
		}

		this.setScanProgress({
			isScanning: true,
			percent: 100,
			status: 'Broadcast sent — refreshing balance…',
		});

		// Best-effort refresh after send (sets its own progress; resets to idle).
		try {
			await this.refreshBalance();
		} catch (error) {
			console.warn('Balance refresh failed after broadcast:', error);
			this.setScanProgress({
				isScanning: false,
				percent: 100,
				status: 'Scan complete',
			});
		}

		return {
			txid,
			raw: '',
			success: true,
		};
	}

	async generateMnemonic(): Promise<string> {
		await this.ensureWasmInitialized();
		return generateWebZjsSeedPhrase();
	}

	/**
	 * Stamp a recent birthday hint for a wallet whose seed was just *generated*
	 * in this browser, so its first scan starts near the chain tip and skips the
	 * minutes-long backwards auto-detect. Best-effort; never call for imported
	 * seeds (they may have history far below the tip).
	 */
	async rememberFreshWalletBirthday(mnemonic: string, accountIndex: number = 0): Promise<number | null> {
		try {
			this.ensureWasmConfigured();
			let client = this.client;
			if (!client) {
				client = getShieldedTransactionClient(DEFAULT_NETWORK);
				client.setApiKey(resolveZcashApiKey());
			}
			await client.initialize();
			return await client.rememberFreshWalletBirthday(mnemonic, accountIndex);
		} catch (err) {
			console.warn('Could not stamp fresh-wallet birthday (will auto-detect on first scan):', err);
			return null;
		}
	}

	onScanProgress(listener: ScanProgressListener): () => void {
		this.scanListeners.add(listener);
		listener(this.getScanProgress());
		return () => {
			this.scanListeners.delete(listener);
		};
	}

	getScanProgress(): ScanProgress {
		return { ...this.scanProgress };
	}

	private setScanProgress(update: Partial<ScanProgress>): void {
		this.scanProgress = { ...this.scanProgress, ...update };
		for (const listener of this.scanListeners) {
			try {
				listener(this.getScanProgress());
			} catch {
				// Keep progress updates resilient to callback failures.
			}
		}
	}
}

export const winbit32Wallet = new Winbit32WalletService();
export default winbit32Wallet;

